import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';
import { DATE_FORMAT_MM_DOT_DD_DOT_YYYY } from 'constants/formats';
import dayjs from 'dayjs';

import { DatePickerControl } from 'components/_basic/Form/DatePicker';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import {
  EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES,
  prefix,
} from 'components/BusinessToolForm/Sections/ExecutiveSummary/CompanyDescriptionExecSummary/utils/form';
import { OperationIndustry } from 'components/BusinessToolForm/Sections/Global/components/OperationIndustry/OperationIndustry';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

export const CompanyDescriptionExecSum: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = true;

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('executiveSummary.title')}
          badgeText={t('companyDescription')}
          section={FOR_INVESTORS_SECTIONS.EXECUTIVE_SUMMARY}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {/* 1.2.1 */}
            <TextInputControl
              control={control}
              id="executiveSummary.companyDescription.name"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)}
              label={t('executiveSummary.companyDescription.name')}
              disabled={!canAccess}
              maxLength={42}
            />
          </Grid>
          <Grid item xs={6}>
            {/* 1.2.3 */}
            <DatePickerControl
              control={control}
              maxDate={dayjs().subtract(1, 'day')}
              id="executiveSummary.companyDescription.dateOfRegistration"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.REGISTRATION_DATE)}
              label={t('executiveSummary.companyDescription.dateOfRegistration')}
              dateFormat={DATE_FORMAT_MM_DOT_DD_DOT_YYYY}
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={6}>
            {/* 1.2.6 */}
            <TextInputControl
              control={control}
              id="executiveSummary.companyDescription.address"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ADDRESS)}
              label={t('executiveSummary.companyDescription.address')}
              disabled={!canAccess}
              maxLength={1000}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard disabled={!canAccess} title={t('executiveSummary.companyDescription.industry')} collapsible>
        <OperationIndustry
          sectorId="executiveSummary.companyDescription.sector"
          subSectorId="executiveSummary.companyDescription.subsector"
          industryGroupId="executiveSummary.companyDescription.industryGroup"
          industryId="executiveSummary.companyDescription.industry"
          disabled={!canAccess}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.companyDescription.mission')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MISSION)}
      >
        {/* 1.2.5 */}
        <TextInputControl
          multiline
          control={control}
          id="executiveSummary.companyDescription.mission"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MISSION)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.companyDescription.briefHistory')}
        aiFieldName={concatWithDot(prefix, EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.HISTORY)}
      >
        {/* 1.2.7 */}
        <TextInputControl
          multiline
          control={control}
          id="executiveSummary.companyDescription.briefHistory"
          name={concatWithDot(prefix, EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.HISTORY)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.companyDescription.owners')}
        aiFieldName={concatWithDot(prefix, EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.COMPANY_OWNERS)}
      >
        {/* 1.2.8 */}
        <TextInputControl
          multiline
          control={control}
          id="executiveSummary.companyDescription.owners"
          name={concatWithDot(prefix, EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.COMPANY_OWNERS)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.companyDescription.mainResources')}
        aiFieldName={concatWithDot(prefix, EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.RESOURCES_AND_ASSETS)}
      >
        {/* 1.2.10 */}
        <TextInputControl
          multiline
          control={control}
          id="executiveSummary.companyDescription.mainResources"
          name={concatWithDot(prefix, EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.RESOURCES_AND_ASSETS)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
    </>
  );
};
